/**
 * Central catalogue of user-facing messages (German).
 *
 * Every user-facing *message* — toast text via {@link notify}, status lines, the
 * `getErrorMessage` fallback — lives here, so wording stays coherent and is
 * changed in exactly one place. There is no i18n library; this module IS the
 * single German seam for messages. (Static UI chrome — field labels, table
 * headers — is not a "message" and stays inline / in locale.de.ts.)
 *
 * Conventions (keep new entries consistent):
 *   - German only. Impersonal register — no "Sie"/"du"; use the infinitive for
 *     instructions ("Bitte erneut versuchen").
 *   - Outcome messages read `<Nomen> <Partizip>`: "Rechnung erstellt",
 *     "Teilnehmer gelöscht". The participle is lower-case; only nouns are
 *     capitalised.
 *   - No trailing period on single-clause toasts (they are labels, not prose).
 *     Multi-sentence guidance (the generic error, the missing-id hint) is
 *     punctuated normally.
 *   - Terminology (use these exact terms, never English/synonyms):
 *     Seminar · Teilnehmer · Referent · Kunde · Mitarbeiter · Rechnung ·
 *     Zertifikat · Benutzer · E-Mail (plural E-Mails).
 *   - Parameterised messages are functions; everything else is a string.
 */

export const MSG = {
  common: {
    /** Generic fallback when no specific message is known (JS/network errors). */
    error: "Es ist ein Fehler aufgetreten. Bitte erneut versuchen.",
    /** Generic success fallback when the backend confirms without its own message. */
    success: "Aktion erfolgreich",
    noResults: "Keine Ergebnisse",
    /**
     * A list came back at the server's hard cap (config/api.js maxLimit=4000) with
     * more rows matching than were returned — the tail is silently missing. Shown so
     * a truncated list can never masquerade as a complete one. Should be rare: every
     * list is normally scoped/searched down well under the cap.
     */
    listTruncated: (shown: number, total: number) =>
      `Nur ${shown} von ${total} Einträgen geladen — bitte Suche oder Filter eingrenzen.`,
  },

  auth: {
    sessionExpired: "Sitzung abgelaufen. Bitte erneut anmelden.",
  },

  /** Generic create/update outcome for the shared store writer (Referent, Raum, Stichwort …). */
  entity: {
    saved: (label: string, isEdit: boolean) =>
      `${label} ${isEdit ? "aktualisiert" : "hinzugefügt"}`,
  },

  user: {
    saved: (isEdit: boolean) => `Benutzer ${isEdit ? "aktualisiert" : "hinzugefügt"}`,
    deleted: "Benutzer gelöscht",
  },

  seminar: {
    saved: (isEdit: boolean) => `Seminar ${isEdit ? "aktualisiert" : "hinzugefügt"}`,
    newId: (id: number | string) => `Neue Seminar-ID: ${id}`,
    referentAdded: "Referent zum Seminar hinzugefügt",
    referentUpdated: "Honorar für dieses Seminar gespeichert",
    referentDeleted: "Referent gelöscht",
    participantDeleted: "Teilnehmer gelöscht",
  },

  invoice: {
    updated: "Rechnung aktualisiert",
    created: "Rechnung erstellt",
    certificatesCreated: "Zertifikate erstellt",
    noCertificate: "Kein Zertifikat erstellt",
    sent: "Rechnung per E-Mail gesendet",
    certificatesSent: "Zertifikat(e) gesendet",
    reminderSent: "Zahlungserinnerung gesendet",
  },

  client: {
    saved: (isNew: boolean) => `Kunde ${isNew ? "erstellt" : "aktualisiert"}`,
    /** Guard when a stored record is missing its documentId (should not happen). */
    missingDocumentId: "Dokument-ID fehlt. Bitte die Seite neu laden und erneut versuchen.",
  },

  employee: {
    saved: (isNew: boolean) => `Mitarbeiter ${isNew ? "erstellt" : "aktualisiert"}`,
  },

  participant: {
    created: "Teilnehmer erstellt",
    swapped: "Teilnehmer getauscht",
  },

  email: {
    sent: "E-Mail gesendet",
    resent: "E-Mail erneut gesendet",
    deleted: "E-Mails gelöscht",
    deletedOne: "E-Mail gelöscht",
    invalidAddress: "Ungültige E-Mail-Adresse",
    invalidAddresses: "Eine oder mehrere E-Mail-Adressen sind ungültig",
    maxAttachments: (max: number) => `Maximal ${max} Anhänge`,
    maxAttachmentSize: "Maximal 10 MB Anhänge insgesamt",
  },
} as const;
